/**
 * WorkforceScheduleWeek — the prototype's Week view, ported (guide step 9).
 * Staff × day grid for the loaded week, above a five-tile KPI strip, with the
 * two bulk actions the prototype offers (copy last week, generate).
 *
 * Rendered by WorkforceSchedule when its mode is "week" — one route, one rail
 * item, an in-screen Day/Week toggle. Every figure here is derived from the
 * ScheduleWeek the day view already loaded; nothing is fetched twice and
 * nothing is invented. copyPreviousWeek / generateSchedule have no server
 * implementation yet (REQ-096d), so both fail loud.
 */
import { useState } from "react";
import { Btn, Icon, Pill, T, alpha } from "@v0y4ge/jrni-ui";
import { StatTile } from "../../shell/composites";
import { toast } from "../../shell/toast";
import { useIsMobile } from "../../shell/useResponsive";
import { copyPreviousWeek, generateSchedule, type ScheduleWeek, type WfShift } from "./wf-api";
import { weekDates, dayLabel, hmToMin, hmRangeLabel, isoToday, roleHue } from "./wf-shared";
import { useT } from "../../lib/i18n";

const STAFF_W = 232;

/** A refused request often carries no message; never report an empty reason. */
function reasonOf(e: unknown): string {
  const msg = e instanceof Error ? e.message : typeof e === "string" ? e : "";
  return msg.trim() || "the request was refused without a reason";
}

export default function WorkforceScheduleWeek({ week, weekStart, locationId, canWrite, onAddShift, onReload }: {
  week: ScheduleWeek;
  weekStart: string;
  locationId: string | null;
  canWrite: boolean;
  onAddShift: (date: string, staffId: string) => void;
  onReload: () => void;
}) {
  const t = useT();
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState<string | null>(null); // ui-local (in-flight bulk action)
  const days = weekDates(weekStart);
  const today = isoToday();

  const hoursOf = (shifts: WfShift[]) => shifts.reduce((sum, s) => sum + (hmToMin(s.end) - hmToMin(s.start)) / 60, 0);
  const assigned = week.shifts.filter((s) => s.staffId);
  const scheduledHours = hoursOf(assigned);
  const targetHours = week.staff.reduce((sum, s) => sum + (s.hoursTarget || 0), 0);
  const openShifts = week.shifts.filter((s) => !s.staffId).length;
  const overtimeStaff = week.staff.filter((s) => s.hoursScheduled > s.hoursTarget).length;
  const gapDays = days.filter((d) => {
    const dem = week.demand?.find((x) => x.date === d);
    if (!dem) return false;
    const heads = dem.slices.reduce((sum, sl) => sum + sl.events + sl.appointments + sl.queue, 0);
    return heads > 0 && !week.shifts.some((s) => s.date === d && s.staffId);
  }).length;

  const runBulk = async (id: string, action: string, label: string, fn: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(id);
    try {
      await fn();
      toast({ tone: "success", title: label });
      onReload();
    } catch (e) {
      toast({ tone: "danger", title: `${action} ${t("workforce.schedule.isnTAvailable", "isn't available")}`, subtitle: `${id} · ${reasonOf(e)}` });
    } finally { setBusy(null); }
  };

  /** One cell: the staff member's shifts on that day, or an add affordance. */
  const cell = (staffId: string, date: string) => {
    const shifts = week.shifts.filter((s) => s.staffId === staffId && s.date === date);
    const dayOff = shifts.find((s) => s.dayOffLabel);
    if (dayOff) {
      return (
        <div style={{ padding: "8px 10px", minHeight: 56, display: "flex", alignItems: "center", background: `repeating-linear-gradient(135deg, transparent 0 5px, ${alpha(T.n3, 12)} 5px 10px)` }}>
          <Pill tone="neutral">{dayOff.dayOffLabel}</Pill>
        </div>
      );
    }
    if (shifts.length === 0) {
      if (!canWrite || date < today) return <div style={{ minHeight: 56 }} />;
      return (
        <button data-testid="wfweek-add-cell" data-id={`${staffId}|${date}`} onClick={() => onAddShift(date, staffId)}
          style={{ width: "100%", minHeight: 56, border: "none", background: "transparent", color: T.soft, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: T.font }}>
          + {t("workforce.schedule.addShift", "Add shift")}
        </button>
      );
    }
    return (
      <div style={{ padding: "8px 10px", minHeight: 56, display: "flex", flexDirection: "column", gap: 4 }}>
        {shifts.map((s) => (
          <span key={s.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, fontWeight: 600, color: T.n1 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: roleHue(s.role), flexShrink: 0 }} />
            {hmRangeLabel(s.start, s.end)}
            {s.status !== "published" ? <Icon.warn size={10} /> : null}
          </span>
        ))}
      </div>
    );
  };

  const dayIsShort = (date: string) => {
    const dem = week.demand?.find((x) => x.date === date);
    if (!dem) return false;
    const heads = dem.slices.reduce((sum, sl) => sum + sl.events + sl.appointments + sl.queue, 0);
    const staffed = week.shifts.filter((s) => s.date === date && s.staffId).length;
    return heads > 0 && staffed === 0;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="wfweek-root">
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
        <StatTile label={t("workforce.schedule.scheduledHours", "Scheduled hours")} value={`${Math.round(scheduledHours)}/${Math.round(targetHours)}`} icon={<Icon.clock size={14} />} accent={T.p1} />
        <StatTile label={t("workforce.schedule.shiftsThisWeek", "Shifts this week")} value={assigned.length} icon={<Icon.calendar size={14} />} accent={T.tDarkBlue} />
        <StatTile label={t("workforce.schedule.gapDays", "Days with demand, no cover")} value={gapDays} icon={<Icon.warn size={14} />} accent={gapDays > 0 ? T.red1 : T.green1} />
        <StatTile label={t("workforce.schedule.overTarget", "Over hours target")} value={overtimeStaff} icon={<Icon.users size={14} />} accent={overtimeStaff > 0 ? T.orange1 : T.green1} />
        <StatTile label={t("workforce.schedule.openShiftsWeek", "Open shifts (week)")} value={openShifts} icon={<Icon.plus size={14} />} accent={T.orange1} />
      </div>

      {canWrite ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Btn variant="secondary" testid="wfweek-copy-last" disabled={busy !== null}
            onClick={() => { void runBulk("copyPreviousWeek", t("workforce.schedule.copyFromLastWeek", "Copy from last week"), t("workforce.schedule.copiedFromLastWeek", "Copied from last week"), () => copyPreviousWeek(weekStart, locationId ?? undefined)); }}>
            {busy === "copyPreviousWeek" ? t("workforce.schedule.copying", "Copying…") : t("workforce.schedule.copyFromLastWeek", "Copy from last week")}
          </Btn>
          <Btn variant="secondary" icon={<Icon.refresh size={14} />} testid="wfweek-generate" disabled={busy !== null}
            onClick={() => { void runBulk("generateSchedule", t("workforce.schedule.generate", "Generate"), t("workforce.schedule.scheduleGenerated", "Schedule generated"), () => generateSchedule({ weekStart, locationId: locationId ?? undefined })); }}>
            {busy === "generateSchedule" ? t("workforce.schedule.generating", "Generating…") : t("workforce.schedule.generate", "Generate")}
          </Btn>
        </div>
      ) : null}

      {isMobile ? (
        /* Per-day accordion, not a 6-column grid (guide step 11). */
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {days.map((d) => {
            const dayShifts = week.shifts.filter((s) => s.date === d && s.staffId);
            return (
              <details key={d} data-testid="wfweek-day-accordion" data-id={d} open={d === today}
                style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, padding: "10px 14px" }}>
                <summary style={{ cursor: "pointer", minHeight: 44, display: "flex", alignItems: "center", gap: 8, fontSize: 13.5, fontWeight: 700, color: T.n1 }}>
                  {dayLabel(d, "full")}
                  <span style={{ fontSize: 12, fontWeight: 400, color: T.soft }}>{dayShifts.length} {t("workforce.schedule.shiftsLower", "shifts")}</span>
                  {dayIsShort(d) ? <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.red1 }} /> : null}
                </summary>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingTop: 8 }}>
                  {dayShifts.length === 0 ? <span style={{ fontSize: 12, color: T.soft }}>{t("workforce.schedule.nothingScheduled", "Nothing scheduled")}</span> : null}
                  {dayShifts.map((s) => (
                    <span key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.n1 }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: roleHue(s.role), flexShrink: 0 }} />
                      <span style={{ fontWeight: 600 }}>{s.staffName}</span>
                      <span style={{ color: T.soft, marginLeft: "auto" }}>{hmRangeLabel(s.start, s.end)}</span>
                    </span>
                  ))}
                </div>
              </details>
            );
          })}
        </div>
      ) : (
        <div data-testid="wfweek-grid" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: T.shadowSm, overflow: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: `${STAFF_W}px repeat(${days.length}, minmax(120px, 1fr))`, borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, background: T.canvas, zIndex: 2 }}>
            <span style={{ padding: "9px 14px", fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.06, position: "sticky", left: 0, zIndex: 6, background: T.canvas, borderRight: `1px solid ${T.border}` }}>
              {week.staff.length} {t("workforce.schedule.staffLower", "staff")}
            </span>
            {days.map((d) => (
              <span key={d} style={{ padding: "9px 12px", fontSize: 12, fontWeight: 700, color: d === today ? T.p1 : T.n1, display: "inline-flex", alignItems: "center", gap: 6, background: d === today ? alpha(T.p1, 6) : "transparent" }}>
                {dayLabel(d)}
                {dayIsShort(d) ? <span title={t("workforce.schedule.underStaffed", "under-staffed")} style={{ width: 6, height: 6, borderRadius: "50%", background: T.red1 }} /> : null}
              </span>
            ))}
          </div>
          {week.staff.map((st, idx) => (
            <div key={st.id} data-testid="wfweek-staff-row" data-id={st.id}
              style={{ display: "grid", gridTemplateColumns: `${STAFF_W}px repeat(${days.length}, minmax(120px, 1fr))`, borderBottom: idx === week.staff.length - 1 ? "none" : `1px solid ${T.n6}` }}>
              <div style={{ padding: "8px 14px", position: "sticky", left: 0, zIndex: 5, background: T.white, borderRight: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 28, height: 28, borderRadius: "50%", background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{st.initials}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{st.name}</span>
                  <span style={{ display: "block", fontSize: 11, color: T.soft }}>{st.hoursScheduled}/{st.hoursTarget}{t("workforce.schedule.h", "h")}</span>
                </span>
                {(st.skills ?? []).slice(0, 3).map((sk) => (
                  <span key={sk} title={sk} style={{ width: 6, height: 6, borderRadius: "50%", background: roleHue(sk), flexShrink: 0 }} />
                ))}
              </div>
              {days.map((d) => (
                <div key={d} style={{ borderLeft: `1px solid ${T.n6}`, background: d === today ? alpha(T.p1, 4) : T.white }}>{cell(st.id, d)}</div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
